import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import type { BreadcrumbItem } from '../shell/Breadcrumbs';

/**
 * useBreadcrumbs — resolve the top-bar breadcrumb trail for the current path.
 *
 * Detail routes are data-driven, not path-humanized: for every `/<entity>/:id`
 * page the hook resolves the record and builds a correct, consistent parent
 * trail from its foreign keys, so a detail crumb reads like the entity's real
 * ownership chain instead of a fabricated Title-Case slug.
 *
 * Two trail shapes:
 *   - ownership — the record belongs to the Client → Property → Parcel chain
 *     (clients, properties, parcels, tax bills, tax records, documents,
 *     invoices, invoice items, filed returns, appeals, contacts). Rooted at
 *     "Clients", the trail walks whichever ancestors the record's FKs resolve
 *     to and ends at the record's human name. E.g. a document owned by a
 *     client reads "Clients › Alliance Property Tax › Q3 Notice"; a tax bill
 *     reads "Clients › … › <Parcel> › BEX-100001".
 *   - list — a top-level reference/system record with no ownership parent
 *     (assessors, collectors, jurisdictions, parent clients, users,
 *     transmittals). Rooted at the entity's own list, the trail resolves the
 *     record's name: "Assessors › Dallas Central Appraisal District".
 *
 * When a name (or an ancestor) can't be resolved — still loading, a failed
 * fetch, or a missing FK — the crumb falls back to the singular entity name
 * ("Tax bill", "Document") and NEVER a humanized id. Ancestor fetches reuse the
 * detail pages' own query keys (['parcel', id] etc.) so an open detail page and
 * its breadcrumb share one cached request.
 *
 * Every other route (the dashboard, list pages, deep/utility routes) uses the
 * generic capitalize/un-hyphenate mapping, with a `looksLikeId` safety net that
 * renders the singular entity name for any opaque id segment.
 */

interface ClientRow { id: string; name: string | null; short_name: string | null; }
interface PropertyRow { id: string; name: string | null; client_id: string | null; }
interface ParcelRow { id: string; name: string | null; account_number: string | null; property_id: string | null; }
interface InvoiceRow { id: string; invoice_number: string | null; client_id: string | null; }

/**
 * A detail record — a superset of the label + foreign-key fields any detail
 * entity may carry. The hook reads only what a given entity's config extracts;
 * every field is optional so one shape serves them all.
 */
interface DetailRow {
  id?: string;
  name?: string | null;
  short_name?: string | null;
  account_number?: string | null;
  invoice_number?: string | null;
  subject?: string | null;
  title?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  owner_name?: string | null;
  tax_year?: number | null;
  client_id?: string | null;
  property_id?: string | null;
  parcel_id?: string | null;
  invoice_id?: string | null;
  primary_property_id?: string | null;
  primary_parcel_id?: string | null;
  [k: string]: unknown;
}

function humanizeSlug(seg: string): string {
  return seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * An opaque record id (a UUID or a `demo-…` seed slug) — as opposed to a real
 * path word. Humanizing one produces garbage like "Demo Bill Demo Parcel
 * Piedmont 1 1", so a detail crumb on such a segment falls back to the singular
 * of its parent entity ("Tax bill") instead.
 */
function looksLikeId(seg: string): boolean {
  return /^demo-/i.test(seg)
    || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-/i.test(seg)
    || /^[0-9a-f]{16,}$/i.test(seg);
}

function singularEntity(entitySeg: string): string {
  return humanizeSlug(entitySeg).replace(/s$/, '');
}

function genericCrumbs(pathname: string): BreadcrumbItem[] {
  if (pathname === '/' || pathname === '') return [{ label: 'Dashboard' }];
  const segments = pathname.split('/').filter(Boolean);
  return segments.map((seg, i) => {
    const isLast = i === segments.length - 1;
    const label = i > 0 && looksLikeId(seg) ? singularEntity(segments[i - 1]) : humanizeSlug(seg);
    const to = '/' + segments.slice(0, i + 1).join('/');
    return isLast ? { label } : { label, to };
  });
}

type TrailMode = 'ownership' | 'list';

interface DetailConfig {
  /** Endpoint that returns the record. */
  path: (id: string) => string;
  /** Cache key — mirrors the detail page's own key so they share one request. */
  queryKey: (id: string) => unknown[];
  /** The human leaf label, or undefined when unresolved (→ `singular`). */
  leaf: (row: DetailRow | undefined) => string | undefined;
  /** Fallback leaf when the record/name can't be resolved. Never an id. */
  singular: string;
  mode: TrailMode;
  /** list mode: the root crumb (the entity's own list). */
  rootList?: BreadcrumbItem;
  /** ownership mode: how to read each ancestor FK off the record. */
  parcelIdOf?: (row: DetailRow) => string | null | undefined;
  propertyIdOf?: (row: DetailRow) => string | null | undefined;
  clientIdOf?: (row: DetailRow) => string | null | undefined;
  invoiceIdOf?: (row: DetailRow) => string | null | undefined;
}

const contactName = (r: DetailRow): string | undefined =>
  [r.first_name, r.last_name].filter(Boolean).join(' ') || r.title || undefined;

const userName = (r: DetailRow): string | undefined =>
  [r.first_name, r.last_name].filter(Boolean).join(' ') || r.email || undefined;

/**
 * Per-entity detail-route resolvers, keyed by the first path segment. The three
 * core ownership entities (clients/properties/parcels) are handled inline below
 * — everything else that has a resolvable name/parent lives here.
 */
const DETAIL_CONFIG: Record<string, DetailConfig> = {
  // ── ownership-rooted (Client › Property › Parcel chain) ──
  'tax-bills': {
    path: (id) => `/api/tax-bills/${id}`,
    queryKey: (id) => ['tax_bills', id],
    leaf: (r) => r?.account_number || undefined,
    singular: 'Tax bill',
    mode: 'ownership',
    parcelIdOf: (r) => r.parcel_id,
  },
  'tax-records': {
    path: (id) => `/api/tax-records/${id}`,
    queryKey: (id) => ['tax_records', id],
    leaf: (r) => r?.account_number || undefined,
    singular: 'Tax record',
    mode: 'ownership',
    parcelIdOf: (r) => r.parcel_id,
  },
  // Part 2 §1.4 — without this the route fell through to genericCrumbs and read
  // "Parcel Values › Parcel Value", whose first crumb linked to /parcel-values,
  // a route that does not exist. A user who arrives by clicking edit on a tax-year
  // box on the Values tab was stranded here with no way back to the parcel.
  // Ownership mode is the house pattern (same as tax-bills), so the trail carries
  // the real Client › Property › Parcel chain rather than a fabricated one.
  'parcel-values': {
    path: (id) => `/api/parcel-values/${id}`,
    queryKey: (id) => ['parcel-value', id],
    leaf: (r) => {
      if (!r) return undefined;
      const type = typeof r.value_type === 'string' && r.value_type
        ? r.value_type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
        : null;
      if (r.tax_year && type) return `${r.tax_year} ${type}`;
      return type || (r.tax_year ? `Value · ${r.tax_year}` : undefined);
    },
    singular: 'Parcel value',
    mode: 'ownership',
    parcelIdOf: (r) => r.parcel_id,
  },
  documents: {
    path: (id) => `/api/documents/${id}`,
    queryKey: (id) => ['documents', id],
    leaf: (r) => r?.name || undefined,
    singular: 'Document',
    mode: 'ownership',
    parcelIdOf: (r) => r.parcel_id,
    propertyIdOf: (r) => r.property_id,
    clientIdOf: (r) => r.client_id,
  },
  invoices: {
    path: (id) => `/api/invoices/${id}`,
    queryKey: (id) => ['invoices', id],
    leaf: (r) => r?.invoice_number || undefined,
    singular: 'Invoice',
    mode: 'ownership',
    clientIdOf: (r) => r.client_id,
  },
  'invoice-items': {
    path: (id) => `/api/invoice-items/${id}`,
    queryKey: (id) => ['invoice-items', id],
    leaf: () => undefined,
    singular: 'Invoice item',
    mode: 'ownership',
    invoiceIdOf: (r) => r.invoice_id,
    clientIdOf: (r) => r.client_id,
  },
  'filed-returns': {
    path: (id) => `/api/filed-returns/${id}`,
    queryKey: (id) => ['filed_returns', id],
    leaf: (r) => r?.account_number || r?.owner_name || undefined,
    singular: 'Filed return',
    mode: 'ownership',
    parcelIdOf: (r) => r.primary_parcel_id,
    propertyIdOf: (r) => r.primary_property_id,
    clientIdOf: (r) => r.client_id,
  },
  appeals: {
    path: (id) => `/api/appeals/${id}`,
    queryKey: (id) => ['appeal', id],
    leaf: (r) => (r?.tax_year ? `Appeal · ${r.tax_year}` : undefined),
    singular: 'Appeal',
    mode: 'ownership',
    parcelIdOf: (r) => r.parcel_id,
  },
  contacts: {
    path: (id) => `/api/contacts/${id}`,
    queryKey: (id) => ['contacts', id],
    leaf: (r) => (r ? contactName(r) : undefined),
    singular: 'Contact',
    mode: 'ownership',
    clientIdOf: (r) => r.client_id,
  },
  'invoice-terms': {
    path: (id) => `/api/invoice-terms/${id}`,
    queryKey: (id) => ['invoice_term', id],
    leaf: (r) => r?.name || undefined,
    singular: 'Fee terms',
    mode: 'ownership',
    // A term is usually client-scoped; a NULL client is a state/org-default
    // term (fee waterfall) — it still resolves its own name at the leaf.
    propertyIdOf: (r) => r.property_id,
    clientIdOf: (r) => r.client_id,
  },

  // ── list-rooted (top-level reference / system records) ──
  assessors: {
    path: (id) => `/api/assessors/${id}`,
    queryKey: (id) => ['assessors', id],
    leaf: (r) => r?.name || undefined,
    singular: 'Assessor',
    mode: 'list',
    rootList: { label: 'Assessors', to: '/assessors' },
  },
  collectors: {
    path: (id) => `/api/collectors/${id}`,
    queryKey: (id) => ['collectors', id],
    leaf: (r) => r?.name || undefined,
    singular: 'Collector',
    mode: 'list',
    rootList: { label: 'Collectors', to: '/collectors' },
  },
  jurisdictions: {
    path: (id) => `/api/jurisdictions/${id}`,
    queryKey: (id) => ['jurisdictions', id],
    leaf: (r) => r?.name || undefined,
    singular: 'Jurisdiction',
    mode: 'list',
    rootList: { label: 'Jurisdictions', to: '/jurisdictions' },
  },
  'parent-clients': {
    path: (id) => `/api/parent-clients/${id}`,
    queryKey: (id) => ['parent-clients', id],
    leaf: (r) => r?.name || r?.short_name || undefined,
    singular: 'Parent client',
    mode: 'list',
    rootList: { label: 'Parent Clients', to: '/parent-clients' },
  },
  users: {
    path: (id) => `/api/users/${id}`,
    queryKey: (id) => ['users', id],
    leaf: (r) => (r ? userName(r) : undefined),
    singular: 'User',
    mode: 'list',
    rootList: { label: 'Users', to: '/users' },
  },
  transmittals: {
    path: (id) => `/api/transmittals/${id}`,
    queryKey: (id) => ['transmittal', id],
    leaf: (r) => r?.subject || undefined,
    singular: 'Transmittal',
    mode: 'list',
    // A transmittal is a tax-bill status; its list surface redirects to Tax Bills.
    rootList: { label: 'Tax Bills', to: '/tax-bills' },
  },
};

/**
 * Second path segments that are NOT record ids — create wizards and static
 * sub-routes — so `/documents/review-queue`, `/appeals/queues`,
 * `/clients/new` and `/parcels/exemption-proposals` are never mistaken for a
 * detail record and instead use the generic mapping.
 */
const NON_ID_SEGMENTS = new Set(['new', 'review-queue', 'queues', 'exemption-proposals']);

export function useBreadcrumbs(pathname: string): BreadcrumbItem[] {
  const segments = pathname.split('/').filter(Boolean);
  const entity = segments[0];
  const idSeg = segments[1];

  // A detail route is exactly `/<entity>/<id>` where the id segment is a real
  // record id — not a create wizard or a static sub-route. Any other shape
  // (deeper paths, list pages, the dashboard) uses the generic mapping.
  const isDetailRoute = segments.length === 2 && !!idSeg && !NON_ID_SEGMENTS.has(idSeg);
  const id = isDetailRoute ? idSeg : '';

  const isClientDetail = isDetailRoute && entity === 'clients';
  const isPropertyDetail = isDetailRoute && entity === 'properties';
  const isParcelDetail = isDetailRoute && entity === 'parcels';
  const config = isDetailRoute && !isClientDetail && !isPropertyDetail && !isParcelDetail
    ? DETAIL_CONFIG[entity]
    : undefined;
  const isConfigDetail = !!config;

  // Hooks must run unconditionally every render; `enabled` gates the fetches so
  // only the chain the current route needs actually requests.

  // The record at a config-driven detail route (tax bill, document, invoice, …).
  const primaryQ = useQuery<DetailRow>({
    queryKey: config ? config.queryKey(id) : ['breadcrumb-primary', 'disabled'],
    queryFn: ({ signal }) => api.get<DetailRow>(config!.path(id), signal),
    enabled: isConfigDetail,
    staleTime: 60_000,
    retry: false,
  });
  const primary = isConfigDetail ? primaryQ.data : undefined;

  // Invoice ancestor — only invoice items chain through an invoice.
  const invoiceId = config?.invoiceIdOf && primary ? (config.invoiceIdOf(primary) ?? undefined) : undefined;
  const invoiceQ = useQuery<InvoiceRow>({
    queryKey: ['invoice', invoiceId],
    queryFn: ({ signal }) => api.get<InvoiceRow>(`/api/invoices/${invoiceId}`, signal),
    enabled: !!invoiceId,
    staleTime: 60_000,
    retry: false,
  });
  const invoice = invoiceQ.data;

  // Parcel — the parcel detail itself, or an ancestor of a config record.
  const configParcelId = config?.parcelIdOf && primary ? (config.parcelIdOf(primary) ?? undefined) : undefined;
  const parcelId = isParcelDetail ? id : configParcelId;
  const parcelQ = useQuery<ParcelRow>({
    queryKey: ['parcel', parcelId],
    queryFn: ({ signal }) => api.get<ParcelRow>(`/api/parcels/${parcelId}`, signal),
    enabled: !!parcelId,
    staleTime: 60_000,
    retry: false,
  });
  const parcel = parcelQ.data;

  // Property — the property detail itself, an ancestor via the parcel, or a
  // config record's direct property FK.
  const configPropertyId = config?.propertyIdOf && primary ? (config.propertyIdOf(primary) ?? undefined) : undefined;
  const propertyId = isPropertyDetail ? id : (parcel?.property_id ?? configPropertyId ?? undefined);
  const propertyQ = useQuery<PropertyRow>({
    queryKey: ['property', propertyId],
    queryFn: ({ signal }) => api.get<PropertyRow>(`/api/properties/${propertyId}`, signal),
    enabled: !!propertyId,
    staleTime: 60_000,
    retry: false,
  });
  const property = propertyQ.data;

  // Client — the client detail itself, an ancestor via the property, or a
  // config record's direct client FK (or its invoice's client).
  const configClientId = config?.clientIdOf && primary ? (config.clientIdOf(primary) ?? undefined) : undefined;
  const clientId = isClientDetail
    ? id
    : (property?.client_id ?? configClientId ?? invoice?.client_id ?? undefined);
  const clientQ = useQuery<ClientRow>({
    queryKey: ['client', clientId],
    queryFn: ({ signal }) => api.get<ClientRow>(`/api/clients/${clientId}`, signal),
    enabled: !!clientId,
    staleTime: 60_000,
    retry: false,
  });
  const client = clientQ.data;

  const clientLabel = (c: ClientRow) => c.name || c.short_name || 'Client';
  const invoiceLabel = (inv: InvoiceRow) => inv.invoice_number || 'Invoice';

  // ── core ownership entities ──
  if (isClientDetail) {
    return [
      { label: 'Clients', to: '/clients' },
      { label: client ? clientLabel(client) : singularEntity('clients') },
    ];
  }

  if (isPropertyDetail) {
    const crumbs: BreadcrumbItem[] = [{ label: 'Clients', to: '/clients' }];
    if (client) crumbs.push({ label: clientLabel(client), to: `/clients/${client.id}` });
    crumbs.push({ label: property?.name || singularEntity('properties') });
    return crumbs;
  }

  if (isParcelDetail) {
    const crumbs: BreadcrumbItem[] = [{ label: 'Clients', to: '/clients' }];
    if (client) crumbs.push({ label: clientLabel(client), to: `/clients/${client.id}` });
    if (property) crumbs.push({ label: property.name || 'Property', to: `/properties/${property.id}` });
    crumbs.push({ label: parcel?.name || parcel?.account_number || singularEntity('parcels') });
    return crumbs;
  }

  // ── config-driven detail entities ──
  if (config) {
    const leafLabel = config.leaf(primary) || config.singular;

    if (config.mode === 'list') {
      const crumbs: BreadcrumbItem[] = [];
      if (config.rootList) crumbs.push(config.rootList);
      crumbs.push({ label: leafLabel });
      return crumbs;
    }

    // ownership mode — root at Clients and walk whichever ancestors resolved.
    const crumbs: BreadcrumbItem[] = [{ label: 'Clients', to: '/clients' }];
    if (client) crumbs.push({ label: clientLabel(client), to: `/clients/${client.id}` });
    if (property) crumbs.push({ label: property.name || 'Property', to: `/properties/${property.id}` });
    if (parcel) crumbs.push({ label: parcel.name || parcel.account_number || 'Parcel', to: `/parcels/${parcel.id}` });
    if (invoice) crumbs.push({ label: invoiceLabel(invoice), to: `/invoices/${invoice.id}` });
    crumbs.push({ label: leafLabel });
    return crumbs;
  }

  return genericCrumbs(pathname);
}
